import React from 'react';
import { Link } from 'react-router-dom';
import { Banknote, CreditCard, Timer, CheckCircle2, ArrowRight } from 'lucide-react';
import { AnimatedSection, AnimatedGrid, AnimatedGridItem } from '../components/AnimatedSection';
export function Financing() {
  return <div className="w-full" data-id="element-144">
      {/* HERO SECTION */}
      <section className="relative bg-navy text-white py-24 md:py-32 overflow-hidden" data-id="element-145">
        <div className="absolute inset-0 z-0" data-id="element-146">
          <div className="absolute inset-0 bg-navy/90 mix-blend-multiply" data-id="element-147"></div>
          <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-148"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center" data-id="element-149">
          <AnimatedSection data-id="element-150">
            <Banknote className="w-12 h-12 text-accent mx-auto mb-6" data-id="element-151" />
            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6 max-w-4xl mx-auto" data-id="element-152">
              Flexible <span className="text-accent" data-id="element-153">Financing Options</span>{' '}
              Available
            </h1>
            <p className="text-lg md:text-xl text-slate-300 max-w-2xl mx-auto" data-id="element-154">
              We offer both in-house financing and third-party financing options
              to help make electrical projects more affordable and accessible.
            </p>
          </AnimatedSection>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-2 bg-accent" data-id="element-155"></div>
      </section>

      {/* OVERVIEW SECTION */}
      <section className="py-20 md:py-28 bg-white" data-id="element-156">
        <div className="max-w-7xl mx-auto px-6" data-id="element-157">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-158">
            <AnimatedSection data-id="element-159">
              <h2 className="font-display text-3xl md:text-4xl font-bold text-navy mb-6 tracking-tight" data-id="element-160">
                Financing Overview
              </h2>
              <div className="w-20 h-1.5 bg-accent mb-8" data-id="element-161"></div>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed" data-id="element-162">
                Don't let budget constraints delay important electrical safety
                upgrades or necessary repairs. We believe that safe, reliable
                electrical systems should be accessible to everyone.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed" data-id="element-163">
                Our financing programs are designed to provide you with flexible
                payment options that fit your monthly budget, allowing you to
                get the work done now and pay over time.
              </p>

              <div className="space-y-4 mb-8" data-id="element-164">
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-sm border border-slate-100" data-id="element-165">
                  <Banknote className="w-6 h-6 text-accent shrink-0" data-id="element-166" />
                  <div data-id="element-167">
                    <h4 className="font-bold text-navy" data-id="element-168">In-House Financing</h4>
                    <p className="text-sm text-slate-600" data-id="element-169">
                      Direct financing options available for qualifying
                      projects.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-sm border border-slate-100" data-id="element-170">
                  <CreditCard className="w-6 h-6 text-accent shrink-0" data-id="element-171" />
                  <div data-id="element-172">
                    <h4 className="font-bold text-navy" data-id="element-173">
                      Third-Party Financing
                    </h4>
                    <p className="text-sm text-slate-600" data-id="element-174">
                      Partnered with trusted lenders for competitive rates and
                      terms.
                    </p>
                  </div>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection delay={0.2} className="bg-navy p-10 rounded-sm text-white relative overflow-hidden" data-id="element-175">
              <div className="absolute inset-0 bg-grid-pattern opacity-10" data-id="element-176"></div>
              <div className="relative z-10" data-id="element-177">
                <h3 className="font-display text-2xl font-bold mb-6" data-id="element-178">
                  Benefits of Financing
                </h3>
                <ul className="space-y-4" data-id="element-179">
                  {['Get necessary electrical work done immediately', 'Preserve your cash and credit lines', 'Predictable monthly payments', 'Competitive interest rates', 'No prepayment penalties (on select plans)', 'Quick and easy application process'].map((benefit, i) => <li key={i} className="flex items-start gap-3" data-id="element-180">
                      <CheckCircle2 className="w-5 h-5 text-accent shrink-0 mt-0.5" data-id="element-181" />
                      <span className="text-slate-300" data-id="element-182">{benefit}</span>
                    </li>)}
                </ul>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-20 bg-slate-50 border-t border-slate-200" data-id="element-183">
        <div className="max-w-7xl mx-auto px-6" data-id="element-184">
          <AnimatedSection className="text-center max-w-3xl mx-auto mb-16" data-id="element-185">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-navy mb-6 tracking-tight" data-id="element-186">
              Easy Application Process
            </h2>
            <div className="w-20 h-1.5 bg-accent mx-auto mb-6" data-id="element-187"></div>
          </AnimatedSection>

          <AnimatedGrid className="grid md:grid-cols-3 gap-8" data-id="element-188">
            {[{
            step: '01',
            title: 'Get an Estimate',
            desc: "First, we'll provide a detailed estimate for your electrical project."
          }, {
            step: '02',
            title: 'Apply for Financing',
            desc: "We'll help you submit a quick application with our financing partners."
          }, {
            step: '03',
            title: 'Get Approved & Start',
            desc: 'Receive fast approval decisions so we can begin your project without delay.'
          }].map((item, i) => <AnimatedGridItem key={i} className="bg-white p-8 rounded-sm shadow-sm border border-slate-100 text-center relative" data-id="element-189">
                <div className="text-5xl font-display font-bold text-slate-100 absolute top-4 right-4" data-id="element-190">
                  {item.step}
                </div>
                <Timer className="w-10 h-10 text-accent mx-auto mb-6 relative z-10" data-id="element-191" />
                <h3 className="font-display font-bold text-xl text-navy mb-3 relative z-10" data-id="element-192">
                  {item.title}
                </h3>
                <p className="text-slate-600 relative z-10" data-id="element-193">{item.desc}</p>
              </AnimatedGridItem>)}
          </AnimatedGrid>

          <AnimatedSection delay={0.3} className="text-center mt-16" data-id="element-194">
            <Link to="/quote" className="bg-accent hover:bg-accent-hover text-white px-8 py-4 rounded-sm font-bold text-lg transition-colors inline-flex items-center gap-2" data-id="element-195">
              Request Information <ArrowRight className="w-5 h-5" data-id="element-196" />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>;
}